import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Subject,
  concatMap,
  debounceTime,
  tap,
  catchError,
  EMPTY,
  Observable,
  of,
  map,
} from 'rxjs';
import { StudentAssignmentApiService } from './student-assignment-api.service';
import { StudentAssignmentStateService } from './student-assignment-state.service';
import { SaveAnswerDto } from './student-assignment.models';

@Injectable({
  providedIn: 'root',
})
export class StudentAnswerService {
  private readonly apiService = inject(StudentAssignmentApiService);
  private readonly state = inject(StudentAssignmentStateService);
  private readonly destroyRef = inject(DestroyRef);

  // Debounced answer save subject (only for typing debounce, NOT navigation tracking)
  private readonly answerSaveSubject$$ = new Subject<{
    questionId: string;
    dto: SaveAnswerDto;
  }>();

  // Track the latest pending answer that may not yet be persisted to the backend
  private pendingAnswer: { questionId: string; dto: SaveAnswerDto } | null =
    null;

  constructor() {
    // Set up debounced answer saving
    this.answerSaveSubject$$
      .pipe(
        debounceTime(500),
        concatMap(({ questionId, dto }) => {
          const shareCode = this.state.shareCode;
          if (!shareCode) return EMPTY;

          return this.apiService
            .saveAnswer(shareCode, questionId, dto)
            .pipe(
              tap((submission) => {
                // Update submission state with server response
                this.state.setSubmission(submission);
              }),
              catchError((err) => {
                console.error('Failed to save answer:', err);
                // Don't show error to user for background saves
                return EMPTY;
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // ============================================
  // QUESTION NAVIGATION
  // ============================================

  navigateToQuestion(index: number): void {
    if (index < 0 || index >= this.state.totalQuestions) return;

    const question = this.state.questions[index];
    if (!question) return;

    this.state.setCurrentQuestionIndex(index);

    // Track the view via direct API call (separate from debounced answer saves)
    const shareCode = this.state.shareCode;
    if (shareCode) {
      const now = new Date().toISOString();
      this.apiService
        .saveAnswer(shareCode, question.id, { viewedAt: now })
        .pipe(
          tap((submission) => this.state.setSubmission(submission)),
          catchError((err) => {
            console.error('Failed to track question view:', err);
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  nextQuestion(): void {
    if (!this.state.isLastQuestion) {
      this.flushPendingAnswerSync();
      this.navigateToQuestion(this.state.currentQuestionIndex + 1);
    }
  }

  prevQuestion(): void {
    if (this.state.currentQuestionIndex > 0) {
      this.flushPendingAnswerSync();
      this.navigateToQuestion(this.state.currentQuestionIndex - 1);
    }
  }

  // ============================================
  // ANSWER MANAGEMENT
  // ============================================

  saveAnswer(questionId: string, answer: SaveAnswerDto): void {
    // Update local state immediately (optimistic)
    const localAnswers = new Map(this.state.getLocalAnswers());
    const existing = localAnswers.get(questionId) || {};
    localAnswers.set(questionId, { ...existing, ...answer });
    this.state.setLocalAnswers(localAnswers);

    const dto = {
      ...answer,
      viewedAt: new Date().toISOString(),
    };

    // Track as pending (not yet persisted to backend)
    this.pendingAnswer = { questionId, dto };

    // Debounced save to backend
    this.answerSaveSubject$$.next({ questionId, dto });
  }

  getLocalAnswer(questionId: string): SaveAnswerDto | undefined {
    return this.state.getLocalAnswers().get(questionId);
  }

  /**
   * Flush pending answer and return Observable that completes when saved.
   * Use before submit to guarantee the latest answer is persisted.
   */
  flushPendingAnswer(): Observable<void> {
    if (!this.pendingAnswer) return of(undefined);

    const { questionId, dto } = this.pendingAnswer;
    this.pendingAnswer = null;

    const shareCode = this.state.shareCode;
    if (!shareCode) return of(undefined);

    return this.apiService.saveAnswer(shareCode, questionId, dto).pipe(
      tap((submission) => this.state.setSubmission(submission)),
      map(() => undefined),
      catchError((err) => {
        console.error('Failed to flush answer:', err);
        return of(undefined);
      }),
    );
  }

  /**
   * Immediately save pending answer (fire-and-forget).
   * Used before navigation to prevent data loss without blocking the UI.
   */
  private flushPendingAnswerSync(): void {
    if (!this.pendingAnswer) return;

    const { questionId, dto } = this.pendingAnswer;
    this.pendingAnswer = null;

    const shareCode = this.state.shareCode;
    if (!shareCode) return;

    this.apiService
      .saveAnswer(shareCode, questionId, dto)
      .pipe(
        tap((submission) => this.state.setSubmission(submission)),
        catchError((err) => {
          console.error('Failed to flush answer:', err);
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
